import { Building2, UserCog, CalendarDays, Mail, BadgeCheck } from "lucide-react";
import { Drawer } from "../../../common/comms/Drawer";
import { EmployeeAvatar } from "./EmployeeAvatar";
import { formatDate, displayValue } from "./employeeListUtils";

function StatusBadge({ employee }) {
  if (!employee.is_verified) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
        Awaiting Verification
      </span>
    );
  }
  if (employee.current_roles.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
        <span className="w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
        No Active Role
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
      Active
    </span>
  );
}

function RoleCard({ role, employee, canAssignManager, onAssignManager }) {
  const canManage = canAssignManager && employee.is_verified && !!role.employee_role_id;
  const manager = displayValue(role.manager_name);

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
      {/* Role + Department */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 leading-tight">
            {role.role_title || "—"}
          </p>
          {role.department_title && (
            <p className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
              <Building2 className="w-3 h-3 flex-shrink-0" />
              {role.department_title}
            </p>
          )}
        </div>
        {role.is_primary && (
          <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
            Primary
          </span>
        )}
      </div>

      {/* Meta row: Manager + Start date */}
      <div className="flex flex-wrap gap-x-5 gap-y-1.5">
        <div className="flex items-center gap-1.5 text-xs text-gray-600">
          <UserCog className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
          <span className="font-medium text-gray-400 mr-0.5">Manager</span>
          {manager ? (
            <span>{manager}</span>
          ) : (
            <span className="text-gray-300">Unassigned</span>
          )}
        </div>
        {role.active_from && (
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <CalendarDays className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span className="font-medium text-gray-400 mr-0.5">Since</span>
            {formatDate(role.active_from)}
          </div>
        )}
      </div>

      {/* Assign manager action */}
      {canAssignManager && (
        <div className="pt-1 border-t border-gray-200">
          <button
            type="button"
            disabled={!canManage}
            onClick={() => canManage && onAssignManager(employee, role)}
            title={canManage ? "Assign or change manager" : "No role assignment id available"}
            className={`text-xs font-medium flex items-center gap-1 transition-colors ${
              canManage
                ? "text-orange-primary hover:underline"
                : "text-gray-300 cursor-not-allowed"
            }`}
          >
            <UserCog className="w-3.5 h-3.5" />
            {manager ? "Change manager" : "Assign manager"}
          </button>
        </div>
      )}
    </div>
  );
}

export function EmployeeDrawer({
  employee,
  onClose,
  canVerify,
  canUpdateRole,
  canAssignManager,
  canDelete,
  onVerify,
  onManageRoles,
  onAssignManager,
  onDelete,
}) {
  const roles = employee?.current_roles || [];

  return (
    <Drawer
      isOpen={!!employee}
      onClose={onClose}
      title=""
      width={480}
      bodyClassName="flex-1 overflow-y-auto"
    >
      {employee && (
        <>
          {/* Identity header — orange tinted banner */}
          <div className="bg-light-orange border-b border-orange-100 px-5 py-5">
            <div className="flex items-start gap-4">
              <EmployeeAvatar name={employee.name} size="lg" />
              <div className="min-w-0 flex-1">
                <p className="text-lg font-bold text-gray-900 leading-tight">{employee.name}</p>
                {employee.email && (
                  <p className="flex items-center gap-1.5 text-sm text-gray-500 mt-1">
                    <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                    {employee.email}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {employee.joined_at && (
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <CalendarDays className="w-3.5 h-3.5 flex-shrink-0" />
                      Joined {formatDate(employee.joined_at)}
                    </span>
                  )}
                  <StatusBadge employee={employee} />
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-5 py-5 space-y-6">
            {/* Roles section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                  Roles & Departments
                </p>
                {canUpdateRole && employee.is_verified && (
                  <button
                    type="button"
                    onClick={() => onManageRoles(employee)}
                    className="text-sm font-medium text-orange-primary hover:underline"
                  >
                    Manage roles
                  </button>
                )}
              </div>

              {roles.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center">
                  <p className="text-sm text-gray-500">No active roles</p>
                  {canUpdateRole && employee.is_verified && (
                    <button
                      type="button"
                      onClick={() => onManageRoles(employee)}
                      className="mt-2 text-sm font-medium text-orange-primary hover:underline"
                    >
                      Add a role
                    </button>
                  )}
                  {!employee.is_verified && (
                    <p className="mt-1 text-xs text-gray-400">
                      Verify this employee before assigning roles.
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {roles.map((role, idx) => (
                    <RoleCard
                      key={role.employee_role_id || `${role.role_id || "role"}-${idx}`}
                      role={role}
                      employee={employee}
                      canAssignManager={canAssignManager}
                      onAssignManager={onAssignManager}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Action buttons */}
            {((canVerify && !employee.is_verified) || (canDelete && employee.employee_id)) && (
              <div className="space-y-2 pt-2 border-t border-gray-100">
                {canVerify && !employee.is_verified && (
                  <button
                    type="button"
                    onClick={() => onVerify(employee)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-orange-primary text-white text-sm font-semibold hover:bg-orange-600 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-primary/50"
                  >
                    <BadgeCheck className="w-4 h-4" />
                    Verify Employee
                  </button>
                )}
                {canDelete && employee.employee_id && (
                  <button
                    type="button"
                    onClick={() => onDelete(employee)}
                    className="w-full py-2.5 px-4 rounded-lg border border-error-red text-error-red text-sm font-semibold hover:bg-error-red/10 transition-colors focus:outline-none focus:ring-2 focus:ring-error-red/30"
                  >
                    Delete Employee
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </Drawer>
  );
}
